/**
 * cartStateParse — shared parsers for the cart's JSON blobs.
 *
 * Extracted from SignStep (S1 punch-list, 2026-06-11) so CommitFlow can
 * resolve the disclosure package + signer roster for the pre-commit
 * /signing/start call without duplicating the parse logic.
 *
 * Source: cart.PeopleJson / cart.ProductsJson / cart.MetadataJson — v3 SQL
 * `Carts` table columns (see apps/PCBJxChangeApi/Data/Entities/Cart.cs).
 */
import type { CartDto } from '@retail-ops/shared-types';
import type { CustomerType } from '@retail-ops/shared-types/enums';
import type { RaoPerson } from '../types';
import type { CartProductLine } from '../disclosure';

/** Parse cart.peopleJson — tolerates both bare-array and `{people:[...]}` shapes. */
export function parsePeople(cart: CartDto): ReadonlyArray<RaoPerson> {
  if (!cart.peopleJson) return [];
  try {
    const raw = JSON.parse(cart.peopleJson) as unknown;
    let arr: unknown[] = [];
    if (Array.isArray(raw)) arr = raw;
    else if (raw && typeof raw === 'object' && Array.isArray((raw as { people?: unknown }).people)) {
      arr = (raw as { people: unknown[] }).people;
    }
    return arr.filter((p): p is RaoPerson => !!p && typeof p === 'object');
  } catch {
    return [];
  }
}

/** Parse cart.productsJson into the disclosure engine's product-line shape. */
export function parseProducts(cart: CartDto): ReadonlyArray<CartProductLine> {
  if (!cart.productsJson) return [];
  try {
    const raw = JSON.parse(cart.productsJson) as unknown;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
      .map((p) => ({
        code: typeof p['code'] === 'string' ? p['code'] : '',
        ...(typeof p['qty'] === 'number' ? { qty: p['qty'] } : {}),
        ...(typeof p['cardTypeCode'] === 'string' ? { cardTypeCode: p['cardTypeCode'] } : {}),
        ...(typeof p['openingDeposit'] === 'number' ? { openingDeposit: p['openingDeposit'] } : {}),
        // Reserved account number stamped by AcctIdGen (C4 / TechReq#21).
        ...(typeof p['acctId'] === 'string' && p['acctId'] ? { acctId: p['acctId'] as string } : {}),
      }))
      .filter((p) => p.code.length > 0);
  } catch {
    return [];
  }
}

/** Pull ownership.entityType out of cart.metadataJson when present. */
export function parseEntityType(cart: CartDto): CustomerType | undefined {
  if (!cart.metadataJson) return undefined;
  try {
    const raw = JSON.parse(cart.metadataJson) as unknown;
    if (!raw || typeof raw !== 'object') return undefined;
    const obj = raw as Record<string, unknown>;
    const nested = obj['ownership'];
    if (nested && typeof nested === 'object') {
      const n = nested as Record<string, unknown>;
      if (typeof n['entityType'] === 'string') return n['entityType'] as CustomerType;
    }
  } catch { /* ignore */ }
  return undefined;
}
